/**
 * London Smoke & Cure — Njord twin sync status and device transparency for Odin UI.
 */

const NJORD_SOFTAP_SSID = 'LSC-Njord-Setup';

/**
 * @param {object|null|undefined} liveOrPresence live/{id} or presence object
 * @returns {object|null}
 */
function njordPresenceNode(liveOrPresence) {
    if (!liveOrPresence || typeof liveOrPresence !== 'object') return null;
    if (liveOrPresence.presence && typeof liveOrPresence.presence === 'object') {
        return liveOrPresence.presence;
    }
    return liveOrPresence;
}

/**
 * Firmware version from live presence (e.g. njord-1.6.7).
 * @param {object|null|undefined} liveOrPresence live/{id} or presence object
 * @returns {string|null}
 */
export function njordFirmwareVersion(liveOrPresence) {
    const presence = njordPresenceNode(liveOrPresence);
    if (!presence) return null;
    const raw = presence.fw;
    if (raw == null) return null;
    const s = String(raw).trim();
    return s || null;
}

/**
 * Station LAN IPv4 from presence (e.g. 192.168.1.42).
 * @param {object|null|undefined} liveOrPresence
 * @returns {string|null}
 */
export function njordDeviceLanIp(liveOrPresence) {
    const presence = njordPresenceNode(liveOrPresence);
    if (!presence) return null;
    const raw = presence.ip;
    if (raw == null) return null;
    const s = String(raw).trim();
    if (!s || s === '0.0.0.0') return null;
    return s;
}

/**
 * Wi-Fi STA MAC from presence (njord-1.7.2+). Normalised AA:BB:CC:DD:EE:FF.
 * @param {object|null|undefined} liveOrPresence
 * @returns {string|null}
 */
export function njordDeviceMac(liveOrPresence) {
    const presence = njordPresenceNode(liveOrPresence);
    if (!presence) return null;
    const raw = presence.mac;
    if (raw == null) return null;
    const hex = String(raw).trim().toUpperCase().replace(/[^0-9A-F]/g, '');
    if (hex.length !== 12) {
        const s = String(raw).trim().toUpperCase();
        return s || null;
    }
    return hex.match(/.{1,2}/g).join(':');
}

/**
 * @param {object|null|undefined} live
 */
export function njordIsSetupMode(live) {
    if (!live) return false;
    return live.presence?.mode === 'setup' || live.health?.setupMode === true;
}

/**
 * @param {object|null|undefined} presence
 * @returns {string|null}
 */
export function njordDeviceLanUrl(presence) {
    const ip = njordDeviceLanIp(presence);
    if (!ip) return null;
    return `http://${ip}/`;
}

/**
 * @param {object|null|undefined} health
 */
export function njordDeviceTransparency(health) {
    if (!health || typeof health !== 'object') {
        return {
            lastAction: '',
            statusDetail: '',
            desiredRevisionKnown: null,
        };
    }
    return {
        lastAction: health.lastAction ? String(health.lastAction) : '',
        statusDetail: health.statusDetail ? String(health.statusDetail) : '',
        desiredRevisionKnown:
            health.desiredRevisionKnown === true
                ? true
                : health.desiredRevisionKnown === false
                  ? false
                  : null,
        loopDurationMs: health.loopDurationMs != null ? Number(health.loopDurationMs) : null,
        lastPanic: health.lastPanic ? String(health.lastPanic) : '',
        rtdbErrors: health.rtdbErrors != null ? Number(health.rtdbErrors) : null,
        ntpSynced: health.ntpSynced === true,
    };
}

/**
 * @param {object|null} desired from RTDB desired/{id}/config
 * @param {object|null} reported from live/{id}/reported
 * @param {object|null|undefined} health from live/{id}/health
 */
export function njordTwinStatus(desired, reported, health) {
    const transparency = njordDeviceTransparency(health);

    if (transparency.desiredRevisionKnown === false && reported) {
        return {
            label: 'Waiting for settings',
            tone: 'unknown',
            pending: false,
            hint: 'The sensor is online but has not loaded its settings from the cloud yet.',
        };
    }

    const want = desired && typeof desired.revision === 'number' ? desired.revision : null;
    const have = reported && typeof reported.configRevision === 'number' ? reported.configRevision : null;
    if (want == null) {
        return { label: 'No settings saved', tone: 'unknown', pending: false };
    }
    if (have == null) {
        return {
            label: 'Settings pending',
            tone: 'caution',
            pending: true,
            desiredRevision: want,
            reportedRevision: null,
            hint: 'The sensor has not confirmed the latest settings yet.',
        };
    }
    if (have < want) {
        return {
            label: 'Updating settings',
            tone: 'caution',
            pending: true,
            desiredRevision: want,
            reportedRevision: have,
            hint: 'Changes usually apply within about a minute.',
        };
    }
    return {
        label: 'Settings up to date',
        tone: 'ok',
        pending: false,
        desiredRevision: want,
        reportedRevision: have,
    };
}

/**
 * Plain-English health story from live node (includes firmware transparency fields).
 * @param {object|null} live live/{id} object
 */
export function njordHealthStory(live) {
    if (!live) {
        return { summary: 'No live data yet', detail: 'The sensor has not connected yet.' };
    }

    if (njordIsSetupMode(live)) {
        const detail =
            live.health?.statusDetail ||
            `Connect a phone or laptop to WiFi “${NJORD_SOFTAP_SSID}” to choose the site WiFi network.`;
        return {
            summary: 'Setup mode — waiting for WiFi',
            detail,
            lastAction: live.health?.lastAction || '',
            lanUrl: null,
        };
    }

    const presence = live.presence || {};
    const health = live.health || {};
    const latest = live.latest || {};
    const transparency = njordDeviceTransparency(health);

    const parts = [];
    if (presence.ssid) parts.push(`On WiFi “${presence.ssid}”`);
    if (presence.rssi != null) parts.push(`signal ${presence.rssi} dBm`);
    if (health.dhtOk === false) parts.push('Sensor read failing');
    else if (health.dhtStale === true) parts.push('Using last good reading');
    if (health.lastError) parts.push(String(health.lastError));
    if (latest.tempC != null) parts.push(`${latest.tempC} °C · ${latest.humPct ?? '—'} %`);

    let detail = transparency.statusDetail || '';
    if (!detail && presence.ip) {
        detail = `On-site address ${presence.ip}`;
    } else if (presence.ip && presence.fw) {
        detail = `${detail} · ${presence.ip}`;
    }

    return {
        summary: parts.length ? parts.join(' · ') : 'Connected, waiting for readings',
        detail,
        lastAction: transparency.lastAction,
        lanUrl: njordDeviceLanUrl(presence),
        transparency,
    };
}
